import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /** molu: 1 quando avança (entra pela direita), -1 quando volta (entra pela esquerda) */
  #sentido = 1;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.#sentido = 1;
    this.current += 1;
  }

  previous() {
    this.#sentido = -1;
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    const saindo = this.refs.slides?.find((slide) => slide.getAttribute('aria-hidden') !== 'true');

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });

    this.#deslizar(saindo, this.refs.slides?.[relativeIndex]);
  }

  /**
   * molu: o aviso desliza de lado na troca, em vez de só esmaecer (23/10/2026).
   * @param {HTMLElement|undefined} saindo
   * @param {HTMLElement|undefined} entrando
   */
  #deslizar(saindo, entrando) {
    if (!saindo || !entrando || saindo === entrando || !entrando.animate) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const desvio = (lado) => `translateX(${lado * 100}%)`;
    const tempo = { duration: 600, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
    entrando.animate([{ transform: desvio(this.#sentido), opacity: 0 }, { transform: 'none', opacity: 1 }], tempo);
    saindo.animate(
      [
        { transform: 'none', opacity: 1, visibility: 'visible' },
        { transform: desvio(-this.#sentido), opacity: 0, visibility: 'visible' },
      ],
      tempo
    );
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
